import { Grid } from "@mui/material";
import React, { useMemo } from "react";
import { t } from "ttag";
import { IDeviceHealthFilters, ISelectedFilters } from "../../models/DeviceHealth.types";
import useStyles from "./DeviceHealthFilterGroup.style";
import HealthFilter from "./HealthFilter";

interface IDeviceHealthFilterGroupProps {
  filters: IDeviceHealthFilters;
  selected: ISelectedFilters;
  onChange: (selected: ISelectedFilters) => void;
}

/**
 * The left filter rail: sales territory -> customer -> site, mirroring the
 * commercial app's AuditsFilterGroup.
 *
 * The panels cascade: a customer is only offered when its territory is selected,
 * and a site only when its customer is. Changing an upper panel resets the ones
 * below it to everything still in scope - predictable, and it avoids stranding a
 * selection the user can no longer see.
 */
const DeviceHealthFilterGroup: React.FC<IDeviceHealthFilterGroupProps> = ({
  filters,
  selected,
  onChange
}) => {
  const classes: any = useStyles();

  const customerOptions = useMemo(
    () =>
      filters.customers.filter(
        ({ salesTerritory }) => !!salesTerritory && selected.salesTerritories.includes(salesTerritory)
      ),
    [filters.customers, selected.salesTerritories]
  );

  const siteOptions = useMemo(
    () => filters.sites.filter(({ customer }) => !!customer && selected.customers.includes(customer)),
    [filters.sites, selected.customers]
  );

  const handleTerritoriesChange = (salesTerritories: string[]) => {
    const customers = filters.customers
      .filter(({ salesTerritory }) => !!salesTerritory && salesTerritories.includes(salesTerritory))
      .map(({ id }) => id);

    const sites = filters.sites
      .filter(({ customer }) => !!customer && customers.includes(customer))
      .map(({ id }) => id);

    onChange({ salesTerritories, customers, sites });
  };

  const handleCustomersChange = (customers: string[]) => {
    const sites = filters.sites
      .filter(({ customer }) => !!customer && customers.includes(customer))
      .map(({ id }) => id);

    onChange({ ...selected, customers, sites });
  };

  const handleSitesChange = (sites: string[]) => {
    onChange({ ...selected, sites });
  };

  return (
    <Grid item className={classes.filterContainer}>
      <HealthFilter
        title={t`Sales Territory`}
        allLabel={t`All Sales Territories`}
        options={filters.salesTerritories}
        selected={selected.salesTerritories}
        onChange={handleTerritoriesChange}
        showSearchBox
      />
      <HealthFilter
        title={t`Customer Name`}
        allLabel={t`All Customer Names`}
        options={customerOptions}
        selected={selected.customers}
        onChange={handleCustomersChange}
        showSearchBox
      />
      <HealthFilter
        title={t`Site Name`}
        allLabel={t`All Site Names`}
        options={siteOptions}
        selected={selected.sites}
        onChange={handleSitesChange}
        showSearchBox
      />
    </Grid>
  );
};

export default DeviceHealthFilterGroup;
